import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';

import {
    Footer,
    Header,
    Meta,
    StickyCTAButtons
} from '$partials/global/index.js';

import { CalendarItem } from '$partials/listings/index.js';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {React.FC}
 */
const CalendarColumnHeaings = () => {
    return (
        <div
            aria-hidden={true}
            className='relative bg-white layout-container'>
            <div className='layout-grid-negative-gutter hidden md:flex md:pb-30'>
                <div className='md:w-1/12 layout-grid-col-padding' />
                <div className='md:flex md:w-11/12'>
                    <p className='type-style-4 w-4/11 layout-grid-col-padding'>Title</p>
                    <p className='type-style-4 w-2/11 layout-grid-col-padding'>Start Date</p>
                    <p className='type-style-4 w-2/11 layout-grid-col-padding'>Location</p>
                    <p className='type-style-4 w-2/11 layout-grid-col-padding'>Type</p>
                </div>
            </div>
        </div>
    );
}

/**
 * @type {React.FC<PropTypes.InferProps<typeof propTypes>>}
 */
const CalendarListingPage = (props) => {
    // references
    const watchedElemRef = useRef();

    const page = props.data.page;

    // state
    const [ headerHeight, setHeaderHeight ] = useState();

    // misc
    let colHeadingPosition = 0;
    if (page?.announcementItems?.edges?.length > 0) {
        colHeadingPosition = 0;
    } else if (page?.eventItems?.edges?.length > 0) {
        colHeadingPosition = 1;
    } else if (page?.courseItems?.edges?.length > 0) {
        colHeadingPosition = 2;
    } else if (page?.programmeItems?.edges?.length > 0) {
        colHeadingPosition = 3;
    }

    return (
        <>
            <Meta
                description={page.searchDescription || page.listingText}
                domain={props.domain}
                image={page.listingImage}
                page={page} />

            <Header
                {...props?.data?.navigation}
                {...props?.data?.ticker}
                headerHeight={(height) => setHeaderHeight(height)}
                pageType={page?.__typename}
                standfirst={page?.standfirst}
                watchedElemRef={watchedElemRef} />

            <StickyCTAButtons
                cta1={page?.stickyCta1?.[0]}
                cta2={page?.stickyCta2?.[0]} />

            <main
                id='main-content'
                style={{marginTop: `${headerHeight}px`}}>
                <section
                    className='layout-container pt-30 sm:pt-20 md:pt-0 mb-60 md:mb-100 title-height'
                    style={{top: `${headerHeight}px`}}>
                    <div className='layout-grid-negative-gutter md:flex'>
                        <div className='md:w-1/12 layout-grid-col-padding' />
                        <div className='sm:w-8/12 md:w-7/12 layout-grid-col-padding'>
                            {page?.title && (
                                <h1 className='type-style-4 pb-30'>{page.title}</h1>
                            )}

                            {page?.heading && (
                                <p className='type-style-1'>{page.heading}</p>
                            )}
                        </div>
                    </div>
                </section>

                <div ref={watchedElemRef}>
                    {/* announcements */}
                    {page?.announcementItems?.edges?.length > 0 && (
                        <section className='relative bg-white layout-container border-t border-t-charcoal'>
                            <div className='layout-grid-negative-gutter md:flex py-40 md:pt-100 md:pb-60'>
                                <div className='md:w-1/12 layout-grid-col-padding' />
                                <div className='md:w-11/12 layout-grid-col-padding'>
                                    <h2 className='type-style-1'>
                                        {page?.announcementsHeading ? page?.announcementsHeading : 'Featured'}
                                    </h2>
                                </div>
                            </div>
                        </section>
                    )}

                    {colHeadingPosition === 0 && (
                        <CalendarColumnHeaings />
                    )}

                    <section
                        aria-label='Featured list'
                        role='feed'>
                        {page?.announcementItems?.edges.map(({ node: item }, idx)  => {
                            return (
                                <article
                                    aria-label={item.title}
                                    aria-posinset={idx}
                                    aria-setsize={page.announcementItems.edges.total}
                                    key={idx}>
                                    <CalendarItem {...item} />
                                </article>
                            );
                        })}
                    </section>

                    {/* events */}
                    {page?.eventItems?.edges?.length > 0 && (
                        <section className='relative bg-white layout-container border-t border-t-charcoal'>
                            <div className='layout-grid-negative-gutter md:flex py-40 md:pt-100 md:pb-60'>
                                <div className='md:w-1/12 layout-grid-col-padding' />
                                <div className='md:w-11/12 layout-grid-col-padding'>
                                    <h2 className='type-style-1'>
                                        {page?.eventsHeading ? page?.eventsHeading : 'Events'}
                                    </h2>
                                </div>
                            </div>
                        </section>
                    )}

                    {colHeadingPosition === 1 && (
                        <CalendarColumnHeaings />
                    )}

                    <section
                        aria-label='Events list'
                        role='feed'>
                        {page?.eventItems.edges.map(({ node: item }, idx)  => {
                            return (
                                <article
                                    aria-label={item.title}
                                    aria-posinset={idx}
                                    aria-setsize={page.eventItems.edges.total}
                                    key={idx}>
                                    <CalendarItem {...item} />
                                </article>
                            );
                        })}
                    </section>

                    {/* courses */}
                    {page?.courseItems?.edges?.length > 0 && (
                        <section className='relative bg-white layout-container border-t border-t-charcoal'>
                            <div className='layout-grid-negative-gutter md:flex py-40 md:pt-100 md:pb-60'>
                                <div className='md:w-1/12 layout-grid-col-padding' />
                                <div className='md:w-11/12 layout-grid-col-padding'>
                                    <h2 className='type-style-1'>
                                        {page?.coursesHeading ? page?.coursesHeading : 'Courses'}
                                    </h2>
                                </div>
                            </div>
                        </section>
                    )}

                    {colHeadingPosition === 2 && (
                        <CalendarColumnHeaings />
                    )}

                    <section
                        aria-label='Course list'
                        role='feed'>
                        {page?.courseItems.edges.map(({ node: item }, idx)  => {
                            return (
                                <article
                                    aria-label={item.title}
                                    aria-posinset={idx}
                                    aria-setsize={page.courseItems.edges.total}
                                    key={idx}>
                                    <CalendarItem {...item} />
                                </article>
                            );
                        })}
                    </section>

                    {/* programmes */}
                    {page?.programmeItems?.edges?.length > 0 && (
                        <section className='relative bg-white layout-container border-t border-t-charcoal'>
                            <div className='layout-grid-negative-gutter md:flex py-40 md:pt-100 md:pb-60'>
                                <div className='md:w-1/12 layout-grid-col-padding' />
                                <div className='md:w-11/12 layout-grid-col-padding'>
                                    <h2 className='type-style-1'>
                                        {page?.programmesHeading ? page?.programmesHeading : 'Programmes'}
                                    </h2>
                                </div>
                            </div>
                        </section>
                    )}

                    {colHeadingPosition === 3 && (
                        <CalendarColumnHeaings />
                    )}

                    <section
                        aria-label='Programme list'
                        role='feed'>
                        {page?.programmeItems.edges.map(({ node: item }, idx)  => {
                            return (
                                <article
                                    aria-label={item.title}
                                    aria-posinset={idx}
                                    aria-setsize={page.programmeItems.edges.total}
                                    key={idx}>
                                    <CalendarItem {...item} />
                                </article>
                            );
                        })}
                    </section>
                </div>
            </main>

            <Footer
                {...props?.data?.footer}
                {...props?.data?.newsletter} />
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const propTypes = {
    data: PropTypes.shape({
        page: PropTypes.object,
        footer: PropTypes.object,
        navigation: PropTypes.object,
        newsletter: PropTypes.object,
        ticker: PropTypes.object,
    }),
    domain: PropTypes.string,
};

CalendarListingPage.propTypes = propTypes;

export default CalendarListingPage;
